/**
 * The links a view hands out and opens (docs/views.md#5-navigation-sections-and-links): `coast.link` builds one to
 * share from the start the view's object hands out, `https://coast.ws/c/<chat>/<view>` in a chat and
 * `https://coast.ws/v/<view>` at its home, and `coast.open` takes one back, in place when it is a section of this view
 * and otherwise as a path or link for the Coast app to open.
 */
export interface Section {
  name: string;
  params: Record<string, string>;
}
const SECTION = /^[a-z][a-z0-9_-]{0,39}$/;

export function strings(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (value && typeof value === "object")
    for (const [key, item] of Object.entries(value)) if (item !== undefined && item !== null) out[key] = String(item);
  return out;
}

/**
 * The link to share a section (`"issue"` or `{ section, params }`) or a record (anything with an `id`, at the section
 * that shows it) of this view; null without a start to build on, as in a link view.
 */
export function shareLink(
  base: string | null | undefined,
  target: unknown,
  sectionOf: (id: string) => string | null,
): string | null {
  return base ? `${base}${sectionLink(target, sectionOf)}` : null;
}

/**
 * The section of this view that `target` names, as a link's fragment, `#issue?id=atl-148`: a section by name, a section
 * with its parameters, or a record by the section that shows it; empty for the view's start.
 */
export function sectionLink(target: unknown, sectionOf: (id: string) => string | null): string {
  let section: string | null = null,
    params: Record<string, string> = {};
  if (typeof target === "string" && SECTION.test(target)) section = target;
  else if (target && typeof target === "object") {
    const value = target as Record<string, unknown>;
    if (typeof value.section === "string" && SECTION.test(value.section)) {
      section = value.section;
      params = strings(value.params);
    } else if (typeof value.id === "string") {
      section = sectionOf(value.id);
      params = section ? { id: value.id } : {};
    }
  }
  const query = new URLSearchParams(params).toString();
  return section ? `#${section}${query ? `?${query}` : ""}` : "";
}

/**
 * What opening `link` means here: a section of this view, in place, whether it comes as `#issue?id=…`, as this view's
 * path, or as the link to share it; another path on this server or on coast.ws, kept relative, for the Coast app; any
 * other http(s) link as it is; or nothing. A home's path, `/v/<view>`, is also taken as `/a/<view>`, which it was
 * until 2026-10-17 and which stored messages and records still hold.
 */
export function resolveLink(
  link: string,
  base: string | null | undefined,
  pageOrigin: string,
): { section: Section } | { open: string } | null {
  let path = link;
  let own: string | null = null;
  if (/^https?:\/\//.test(link)) {
    try {
      const url = new URL(link),
        start = base ? new URL(base) : null;
      if (url.origin === pageOrigin || url.hostname === "coast.ws" || (start && url.origin === start.origin))
        path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
  const startPath = (() => {
    try {
      return base ? new URL(base).pathname : null;
    } catch {
      return null;
    }
  })();
  const ownPath = startPath?.startsWith("/v/") && path.startsWith("/a/") ? `/v/${path.slice(3)}` : path;
  if (path.startsWith("#")) own = path;
  else if (startPath && (ownPath === startPath || ownPath.startsWith(`${startPath}#`)))
    own = ownPath.slice(startPath.length);
  if (own !== null) {
    if (!own) return { section: { name: "", params: {} } };
    const local = /^#([a-z][a-z0-9_-]{0,39})(?:\?(.*))?$/.exec(own);
    return local
      ? { section: { name: local[1]!, params: Object.fromEntries(new URLSearchParams(local[2] ?? "")) } }
      : null;
  }
  return /^(https?:\/\/|\/)/.test(path) ? { open: path } : null;
}

/**
 * Links outside Coast, the http(s) links `resolveLink` hands on as they are, open only after a tap or key press in the
 * view, and one a second at most, so a view can't open a page on its own or a page for every call one tap allows
 * (docs/views.md#5-navigation-sections-and-links). Paths stay Coast's to open, as they always could.
 */
export class OutsideLinks {
  static readonly interval = 1000;
  private last = -Infinity;

  /** Whether `link` may open now, given whether the view was just tapped; an outside link that may opens now. */
  allow(link: string, tapped: boolean, now = Date.now()): boolean {
    if (!/^https?:\/\//.test(link)) return true;
    if (!tapped || now - this.last < OutsideLinks.interval) return false;
    this.last = now;
    return true;
  }
}
